import type { InputState } from './types';
import { devices } from './control-config';

/** Billentyű-kiosztás azonosító. */
export type LayoutId = 'wasd' | 'arrows' | 'ijkl' | 'numpad';

interface KeyBindings {
  up: string[];
  down: string[];
  left: string[];
  right: string[];
  item: string[];
  drift: string[];
  ability: string[];
}

export interface LayoutDef {
  id: LayoutId;
  /** rövid név a beállításokban */
  name: string;
  /** emberi leírás a segítséghez */
  keys: string;
  bindings: KeyBindings;
}

export const LAYOUTS: Record<LayoutId, LayoutDef> = {
  wasd: {
    id: 'wasd',
    name: 'WASD',
    keys: 'W gáz · S fék · A/D kormány · E drift · Q tárgy · R képesség',
    bindings: {
      up: ['KeyW'],
      down: ['KeyS'],
      left: ['KeyA'],
      right: ['KeyD'],
      item: ['KeyQ'],
      drift: ['KeyE'],
      ability: ['KeyR'],
    },
  },
  arrows: {
    id: 'arrows',
    name: 'Nyilak',
    keys: '↑ gáz · ↓ fék · ←/→ kormány · jobb Shift drift · / tárgy · . képesség',
    bindings: {
      up: ['ArrowUp'],
      down: ['ArrowDown'],
      left: ['ArrowLeft'],
      right: ['ArrowRight'],
      item: ['Slash'],
      drift: ['ShiftRight'],
      ability: ['Period'],
    },
  },
  ijkl: {
    id: 'ijkl',
    name: 'IJKL',
    keys: 'I gáz · K fék · J/L kormány · O drift · U tárgy · P képesség',
    bindings: {
      up: ['KeyI'],
      down: ['KeyK'],
      left: ['KeyJ'],
      right: ['KeyL'],
      item: ['KeyU'],
      drift: ['KeyO'],
      ability: ['KeyP'],
    },
  },
  numpad: {
    id: 'numpad',
    name: 'Num',
    keys: 'Num8 gáz · Num5 fék · Num4/6 kormány · Num9 drift · Num7 tárgy · Num+ képesség',
    bindings: {
      up: ['Numpad8'],
      down: ['Numpad5'],
      left: ['Numpad4'],
      right: ['Numpad6'],
      item: ['Numpad7'],
      drift: ['Numpad9'],
      ability: ['NumpadAdd'],
    },
  },
};

export const LAYOUT_ORDER: LayoutId[] = ['wasd', 'arrows', 'ijkl', 'numpad'];
export const DEFAULT_LAYOUTS: LayoutId[] = ['wasd', 'arrows', 'ijkl', 'numpad'];

export const GAMEPAD_HINT = 'Bal kar / D-pad kormány · RT/LT vagy kar fel/le gáz-fék · X tárgy · Y képesség · RB drift';

export const isLayoutId = (value: unknown): value is LayoutId =>
  typeof value === 'string' && (LAYOUT_ORDER as string[]).includes(value);

const padPrev: boolean[][] = [];

export class InputManager {
  private readonly down = new Set<string>();
  private readonly pressed = new Set<string>();
  private readonly states: InputState[] = LAYOUT_ORDER.map(() => ({
    steer: 0,
    throttle: 0,
    brake: 0,
    drift: false,
    useItem: false,
    ability: false,
  }));
  private readonly keydownHandler: (e: KeyboardEvent) => void;
  private readonly keyupHandler: (e: KeyboardEvent) => void;
  private readonly blurHandler: () => void;
  private pads: (Gamepad | null)[] = [];

  constructor(private readonly target: Window) {
    this.keydownHandler = (e) => {
      if (e.repeat) {
        if (isGameKey(e.code)) e.preventDefault();
        return;
      }
      this.down.add(e.code);
      this.pressed.add(e.code);
      if (isGameKey(e.code)) e.preventDefault();
    };
    this.keyupHandler = (e) => {
      this.down.delete(e.code);
    };
    this.blurHandler = () => {
      this.down.clear();
      this.pressed.clear();
    };
    target.addEventListener('keydown', this.keydownHandler);
    target.addEventListener('keyup', this.keyupHandler);
    target.addEventListener('blur', this.blurHandler);
  }

  /** Képkockánként egyszer: összeállítja a játékosok bemenetét. */
  poll(): void {
    this.pads = readPads();
    for (let i = 0; i < 4; i++) {
      const st = this.states[i];
      st.steer = 0;
      st.throttle = 0;
      st.brake = 0;
      st.drift = false;
      st.useItem = false;
      st.ability = false;

      const device = devices[i];
      if (device.kind === 'gamepad') {
        this.pollGamepad(i, device.pad, st);
      } else {
        const b = LAYOUTS[device.layout].bindings;
        const left = b.left.some((k) => this.down.has(k));
        const right = b.right.some((k) => this.down.has(k));
        st.steer = (right ? 1 : 0) - (left ? 1 : 0);
        st.throttle = b.up.some((k) => this.down.has(k)) ? 1 : 0;
        st.brake = b.down.some((k) => this.down.has(k)) ? 1 : 0;
        st.drift = b.drift.some((k) => this.down.has(k));
        st.useItem = b.item.some((k) => this.pressed.has(k));
        st.ability = b.ability.some((k) => this.pressed.has(k));
      }
    }
    this.pressed.clear();
  }

  /**
   * Gamepad: a gáz/fék a kar függőleges tengelyéről, az RT/LT ravaszról és a D-padról is megy,
   * így a tárgy/képesség gomb nyomva tartása nem veszi le a gázt.
   */
  private pollGamepad(index: number, padIndex: number, st: InputState): void {
    const pad = this.pads[padIndex];
    if (!pad) return;

    const btn = (n: number): boolean => pad.buttons[n]?.pressed ?? false;
    const val = (n: number): number => pad.buttons[n]?.value ?? (btn(n) ? 1 : 0);
    const prev = padPrev[index] ?? [];
    const edge = (n: number): boolean => btn(n) && !prev[n];

    const ax = pad.axes[0] ?? 0;
    const ay = pad.axes[1] ?? 0;
    if (Math.abs(ax) > 0.16) st.steer = Math.max(-1, Math.min(1, ax));
    else if (btn(14)) st.steer = -1;
    else if (btn(15)) st.steer = 1;

    const stickThrottle = ay < -0.35 ? Math.min(1, -ay) : 0;
    const stickBrake = ay > 0.35 ? Math.min(1, ay) : 0;
    st.throttle = Math.max(btn(0) ? 1 : 0, val(7), stickThrottle, btn(12) ? 1 : 0);
    st.brake = Math.max(btn(1) ? 1 : 0, val(6), stickBrake, btn(13) ? 1 : 0);
    st.drift = btn(5) || btn(4);
    if (edge(2)) st.useItem = true;
    if (edge(3)) st.ability = true;

    padPrev[index] = pad.buttons.map((b) => b.pressed);
  }

  state(index: number): InputState {
    return this.states[Math.max(0, Math.min(3, index))];
  }

  layoutOf(index: number): LayoutId {
    return devices[Math.max(0, Math.min(3, index))].layout;
  }

  setLayout(index: number, layout: LayoutId): void {
    if (index < 0 || index >= 4) return;
    devices[index] = { ...devices[index], kind: 'keyboard', layout };
  }

  connectedPads(): number {
    return this.pads.filter((p) => p !== null).length;
  }

  dispose(): void {
    this.target.removeEventListener('keydown', this.keydownHandler);
    this.target.removeEventListener('keyup', this.keyupHandler);
    this.target.removeEventListener('blur', this.blurHandler);
  }
}

const isGameKey = (code: string): boolean =>
  code.startsWith('Arrow') ||
  code === 'Space' ||
  code.startsWith('Numpad') ||
  ['KeyW', 'KeyA', 'KeyS', 'KeyD', 'KeyI', 'KeyJ', 'KeyK', 'KeyL', 'Slash', 'ShiftRight', 'Period'].includes(code);

const readPads = (): (Gamepad | null)[] => {
  const out: (Gamepad | null)[] = [null, null, null, null];
  try {
    const pads = navigator.getGamepads?.() ?? [];
    let slot = 0;
    for (const pad of pads) {
      if (!pad || slot >= 4) continue;
      out[slot] = pad;
      slot++;
    }
  } catch {
    /* a gamepad API hiányában csendben maradunk */
  }
  return out;
};
